/**
 * Badge — shadcn/ui primitive (TruSpace adoption, 2026-05-22).
 *
 * Compact pill for tags, counts, status. Note: TruSpace also has
 * the richer `StatusBadge` primitive in this folder with five
 * semantic tones (info / success / warning / danger / neutral). Use
 * `StatusBadge` for status-meaning pills (Order status, Report
 * status), and `Badge` for everything else (counts, tags, generic
 * labels).
 *
 * @see https://ui.shadcn.com/docs/components/badge
 */
import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline:
          'text-foreground border-border',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type BadgeProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants>;

export function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

// See `button.tsx` for the rationale on disabling
// `react-refresh/only-export-components` for variant side-exports.
// eslint-disable-next-line react-refresh/only-export-components
export { badgeVariants };
